import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CreateTestimonialRequestRequest, TestimonialRequestType } from '@clareefai/types';
import { Avatar } from '@clareefai/ui';
import { useGetPromotersQuery, PromoterResponse } from '../../store/promoterApi';
import { useCreateTestimonialRequestMutation, useGetReferenceCallSubmissionsQuery } from '../../store/vendorApi';
import { ChevronLeft, ChevronRight, Check, User, Building, Star, Award, Video, MessageSquare, Phone, Loader } from 'lucide-react';

type ReviewVideoOrOneOnOne = 'REVIEW' | 'VIDEO' | 'ONE_ON_ONE';

interface ReviewTypeRequestFormProps {
  onClose: () => void;
  /** When set, skip step 2 (type selection) and use this type; success navigates to the appropriate list page */
  initialType?: ReviewVideoOrOneOnOne;
}

const ReviewTypeRequestForm: React.FC<ReviewTypeRequestFormProps> = ({ onClose, initialType }) => {
  const navigate = useNavigate();
  const { data: promotersData = [], isLoading: loadingPromoters } = useGetPromotersQuery();
  const totalSteps = initialType ? 2 : 3; // With initialType: 1=promoter, 2=summary; without: 1=promoter, 2=type, 3=summary
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPromoter, setSelectedPromoter] = useState<PromoterResponse | null>(null);
  const [selectedType, setSelectedType] = useState<TestimonialRequestType | null>(initialType ?? null);
  const [error, setError] = useState<string | null>(null);

  // Promoters already in the 1:1 flow (approved frequency, or a submission awaiting
  // vendor review) must not receive a new ONE_ON_ONE request — the backend rejects it too.
  const { data: pendingSubmissions = [] } = useGetReferenceCallSubmissionsQuery({ status: 'PENDING' });
  const pendingSubmissionPromoterIds = new Set(pendingSubmissions.map(s => s.promoterId));
  const isOneOnOneBlocked = (p: PromoterResponse) =>
    Boolean(p.referenceCallFrequency) || pendingSubmissionPromoterIds.has(p.id);

  // Map promoters to include name property and filter eligible ones
  const promoters = promotersData
    .filter(p => {
      const eligibleStatuses = ['review', 'testimonial_ready', 'testimonial_done', 'available'];
      return !p.status || eligibleStatuses.includes(p.status.toLowerCase());
    })
    .filter(p => initialType !== 'ONE_ON_ONE' || !isOneOnOneBlocked(p))
    .map(p => ({
      ...p,
      name: `${p.firstName} ${p.lastName}`
    }));

  const [createRequest, { isLoading: isSubmitting }] = useCreateTestimonialRequestMutation();

  const reviewTypes = [
    { type: 'REVIEW' as const, label: 'Simple Review', description: 'Request a written review from the promoter', icon: Star, color: 'bg-blue-500' },
    { type: 'VIDEO' as const, label: 'Video Testimonial', description: 'Request a video testimonial from the promoter', icon: Video, color: 'bg-purple-500' },
    { type: 'ONE_ON_ONE' as const, label: '1-on-1 Call', description: 'Request a reference call with the promoter', icon: Phone, color: 'bg-green-500' }
  ];

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getSuccessPath = (): string => {
    const type = selectedType ?? initialType;
    if (type === 'VIDEO') return '/testimonials';
    if (type === 'ONE_ON_ONE') return '/1to1-calls';
    return '/use-cases'; // REVIEW or fallback
  };

  const handleSubmit = async () => {
    const typeToUse = selectedType ?? initialType;
    if (!selectedPromoter || !typeToUse) {
      setError('Please select a promoter' + (initialType ? '' : ' and review type'));
      return;
    }

    setError(null);

    try {
      const request: CreateTestimonialRequestRequest = {
        promoterId: selectedPromoter.id!,
        type: typeToUse
      };

      await createRequest(request).unwrap();

      navigate(getSuccessPath());
      onClose();
    } catch (err: any) {
      setError(err.data?.message || err.message || 'Failed to create request');
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 font-semibold ${
            step <= currentStep 
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border-purple-600 text-white' 
              : 'border-gray-300 text-gray-400'
          }`}>
            {step < currentStep ? <Check className="w-6 h-6" /> : step}
          </div>
          {step < totalSteps && (
            <div className={`w-20 h-0.5 ${
              step < currentStep ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-gray-300'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">Select Promoter</h3>
        <p className="text-gray-600">Choose the promoter for the request</p>
      </div>

      {loadingPromoters ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-purple-600" />
          <span className="ml-3 text-gray-600">Loading promoters...</span>
        </div>
      ) : promoters.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No promoters available</h4>
          <p className="text-gray-600">You don't have any eligible promoters for testimonial requests.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[500px] overflow-y-auto">
          {promoters.map((promoter) => (
            <div
              key={promoter.id}
              onClick={() => {
                setSelectedPromoter(promoter);
                // A previously chosen 1:1 type may not be valid for this promoter
                if (selectedType === 'ONE_ON_ONE' && !initialType && isOneOnOneBlocked(promoter)) {
                  setSelectedType(null);
                }
              }}
              className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                selectedPromoter?.id === promoter.id
                  ? 'border-purple-500 bg-purple-50 shadow-lg transform scale-105'
                  : 'border-gray-200 hover:border-purple-300 hover:bg-purple-25'
              }`}
            >
              <div className="flex flex-col items-center text-center space-y-3">
                <Avatar
                  src={promoter.avatar}
                  name={promoter.name}
                  size="lg"
                  className="border-4 border-white shadow-md"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-lg truncate">{promoter.name}</h4>
                  <p className="text-sm text-gray-600 flex items-center justify-center truncate">
                    <Building className="w-4 h-4 mr-1" />
                    {promoter.company}
                  </p>
                  <div className="flex items-center justify-center space-x-4 mt-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Star className="w-4 h-4 mr-1 text-yellow-500" />
                      {promoter.prospectsReferred || 0}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Award className="w-4 h-4 mr-1 text-green-500" />
                      €{promoter.totalRewards || 0}
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      promoter.status === 'review' ? 'bg-blue-100 text-blue-800' :
                      promoter.status === 'testimonial_ready' ? 'bg-green-100 text-green-800' :
                      promoter.status === 'testimonial_done' ? 'bg-purple-100 text-purple-800' :
                      promoter.status === 'available' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {promoter.status === 'review' ? 'Reviewed' :
                       promoter.status === 'testimonial_ready' ? 'Testimonial Ready' :
                       promoter.status === 'testimonial_done' ? 'Testimonial Done' :
                       promoter.status === 'available' ? 'Available' :
                       promoter.status || 'Active'}
                    </span>
                  </div>
                </div>
              </div>
              {selectedPromoter?.id === promoter.id && (
                <div className="absolute top-3 right-3 w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">Select Request Type</h3>
        <p className="text-gray-600">Choose the type of testimonial you want to request</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {reviewTypes.map((typeOption) => {
          const Icon = typeOption.icon;
          const isDisabled =
            typeOption.type === 'ONE_ON_ONE' && !!selectedPromoter && isOneOnOneBlocked(selectedPromoter);
          return (
            <div
              key={typeOption.type}
              onClick={() => !isDisabled && setSelectedType(typeOption.type)}
              className={`relative p-6 border-2 rounded-xl transition-all duration-200 ${
                isDisabled
                  ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                  : selectedType === typeOption.type
                  ? 'border-purple-500 bg-purple-50 shadow-lg cursor-pointer'
                  : 'border-gray-200 hover:border-purple-300 cursor-pointer'
              }`}
            >
              <div className="text-center space-y-4">
                <div className={`w-16 h-16 ${typeOption.color} rounded-full flex items-center justify-center mx-auto`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">{typeOption.label}</h4>
                <p className="text-sm text-gray-600">
                  {isDisabled
                    ? 'This promoter is already approved or pending approval for 1:1 calls'
                    : typeOption.description}
                </p>
              </div>
              {selectedType === typeOption.type && !isDisabled && (
                <div className="absolute top-4 right-4 w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="text-center space-y-8">
      <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
        <Check className="w-12 h-12 text-white" />
      </div>
      
      <div>
        <h3 className="text-3xl font-bold text-gray-900 mb-4">Request Ready to Send!</h3>
        <p className="text-xl text-gray-600 mb-2">Review the details below</p>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center justify-center">
          <User className="w-5 h-5 mr-2 text-purple-600" />
          Request Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-lg">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Promoter:</span>
              <span className="text-gray-900">{selectedPromoter?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Company:</span>
              <span className="text-gray-900">{selectedPromoter?.company}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Request Type:</span>
              <span className="text-gray-900">
                {reviewTypes.find(t => t.type === selectedType)?.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-center space-x-2 text-blue-700">
          <MessageSquare className="w-5 h-5" />
          <span className="font-medium">
            The promoter will receive an email notification with instructions
          </span>
        </div>
      </div>
    </div>
  );

  const isStep1Valid = selectedPromoter !== null;
  const isStep2Valid = initialType ? true : selectedType !== null;
  const isLastStep = currentStep === totalSteps;

  return (
    <div className="space-y-8">
      {renderStepIndicator()}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="min-h-96">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && !initialType && renderStep2()}
        {(currentStep === 2 && initialType) || (currentStep === 3 && !initialType) ? renderStep3() : null}
      </div>

      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={currentStep === 1 ? onClose : handlePrevious}
          className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          {currentStep === 1 ? (
            'Cancel'
          ) : (
            <>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </>
          )}
        </button>

        {!isLastStep ? (
          <button
            type="button"
            onClick={handleNext}
            disabled={
              (currentStep === 1 && !isStep1Valid) ||
              (currentStep === 2 && !isStep2Valid)
            }
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send Request
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default ReviewTypeRequestForm;
